import { useState } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import { StaggerContainer, staggerItemVariants } from '../shared/StaggerContainer'

const ContactDemo = () => {
  const [formData, setFormData] = useState({
    name: '',
    title: '',
    email: '',
    organization: '',
    message: ''
  })
  const [submitted, setSubmitted] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState(null)

  // Check for reduced motion preference
  const prefersReducedMotion =
    typeof window !== 'undefined'
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
      : false

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)
    setError(null)

    try {
      // Create FormData with the same field names as the lets-talk page
      const formDataToSend = new FormData()
      formDataToSend.append('prospect[name]', formData.name)
      formDataToSend.append('title', formData.title)
      formDataToSend.append('prospect[email]', formData.email)
      formDataToSend.append('prospect[organization]', formData.organization)
      formDataToSend.append('prospect[message]', formData.message)

      const baseURL = 'https://engage.sincereapp.com/prospects';
      // const baseURL = 'http://localhost:3000/prospects'; // For local testing

      const response = await fetch(baseURL, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
        },
        body: formDataToSend,
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.errors?.join(', ') || 'Failed to submit form')
      }

      setIsSubmitting(false)
      setSubmitted(true)

      // Reset form after showing success message
      setTimeout(() => {
        setSubmitted(false)
        setFormData({ name: '', title: '', email: '', organization: '', message: '' })
      }, 5000)
    } catch (err) {
      setIsSubmitting(false)
      setError('There was a problem submitting your form. Please try again or email us directly.')
      console.error('Form submission error:', err)
    }
  }

  const inputStyle = {
    width: '100%',
    padding: '1rem',
    fontSize: 'var(--text-base)',
    border: '2px solid var(--color-neutral-200)',
    borderRadius: 'var(--radius-md)',
    transition: 'all 0.3s ease',
    fontFamily: 'inherit'
  }

  const inputFocusStyle = {
    outline: 'none',
    borderColor: 'var(--color-primary-500)',
    boxShadow: '0 0 0 3px var(--color-primary-100)'
  }

  return (
    <section
      id="contact"
      style={{
        padding: 'var(--space-2xl) 0',
        background: 'var(--gradient-hero)'
      }}
    >
      <div className="container">
        <StaggerContainer
          className='mb-8 pt-20 text-center max-w-[600px] mx-auto'
        >
          <motion.h2 variants={staggerItemVariants} className='!text-2xl !mb-4'>
            Ready to Get Started?
          </motion.h2>
          <motion.p
            variants={staggerItemVariants}
            style={{
              fontSize: 'var(--text-lg)',
              color: 'var(--color-neutral-600)'
            }}
          >
            Contact us to schedule a demo and learn how Sincere can help your
            organization create meaningful connections.
          </motion.p>
        </StaggerContainer>

        <div style={{ maxWidth: '600px', margin: '0 auto' }}>
          <AnimatePresence mode="wait">
            {!submitted ? (
              <motion.div
                key="form"
                initial="hidden"
                animate="visible"
                exit={
                  prefersReducedMotion
                    ? { opacity: 0 }
                    : { opacity: 0, scale: 0.95 }
                }
                transition={{ duration: 0.3 }}
              >
                <StaggerContainer
                  as="form"
                  onSubmit={handleSubmit}
                  staggerDelay={0.1}
                  delayChildren={0.2}
                  style={{
                    background: 'white',
                    padding: 'var(--space-2xl)',
                    borderRadius: 'var(--radius-xl)',
                    boxShadow: 'var(--shadow-lg)'
                  }}
                >
                  {/* Name Field */}
                  <motion.div
                    variants={staggerItemVariants}
                    style={{ marginBottom: 'var(--space-lg)' }}
                  >
                    <label
                      htmlFor="name"
                      style={{
                        display: 'block',
                        marginBottom: 'var(--space-xs)',
                        fontWeight: '600',
                        color: 'var(--color-neutral-700)'
                      }}
                    >
                      Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      style={inputStyle}
                      onFocus={(e) =>
                        Object.assign(e.target.style, inputFocusStyle)
                      }
                      onBlur={(e) => {
                        e.target.style.borderColor = 'var(--color-neutral-200)'
                        e.target.style.boxShadow = 'none'
                      }}
                    />
                  </motion.div>

                  {/* Honeypot Field - Hidden from users but visible to bots */}
                  <div
                    style={{
                      position: 'absolute',
                      left: '-9999px',
                      width: '1px',
                      height: '1px',
                      overflow: 'hidden'
                    }}
                    aria-hidden="true"
                  >
                    <input
                      type="text"
                      id="title"
                      name="title"
                      value={formData.title}
                      onChange={handleChange}
                      tabIndex={-1}
                      autoComplete="off"
                    />
                  </div>

                  {/* Email Field */}
                  <motion.div
                    variants={staggerItemVariants}
                    style={{ marginBottom: 'var(--space-lg)' }}
                  >
                    <label
                      htmlFor="email"
                      style={{
                        display: 'block',
                        marginBottom: 'var(--space-xs)',
                        fontWeight: '600',
                        color: 'var(--color-neutral-700)'
                      }}
                    >
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      style={inputStyle}
                      onFocus={(e) =>
                        Object.assign(e.target.style, inputFocusStyle)
                      }
                      onBlur={(e) => {
                        e.target.style.borderColor = 'var(--color-neutral-200)'
                        e.target.style.boxShadow = 'none'
                      }}
                    />
                  </motion.div>

                  {/* Organization Field */}
                  <motion.div
                    variants={staggerItemVariants}
                    style={{ marginBottom: 'var(--space-lg)' }}
                  >
                    <label
                      htmlFor="organization"
                      style={{
                        display: 'block',
                        marginBottom: 'var(--space-xs)',
                        fontWeight: '600',
                        color: 'var(--color-neutral-700)'
                      }}
                    >
                      Organization *
                    </label>
                    <input
                      type="text"
                      id="organization"
                      name="organization"
                      value={formData.organization}
                      onChange={handleChange}
                      required
                      style={inputStyle}
                      onFocus={(e) =>
                        Object.assign(e.target.style, inputFocusStyle)
                      }
                      onBlur={(e) => {
                        e.target.style.borderColor = 'var(--color-neutral-200)'
                        e.target.style.boxShadow = 'none'
                      }}
                    />
                  </motion.div>

                  {/* Message Field */}
                  <motion.div
                    variants={staggerItemVariants}
                    style={{ marginBottom: 'var(--space-xl)' }}
                  >
                    <label
                      htmlFor="message"
                      style={{
                        display: 'block',
                        marginBottom: 'var(--space-xs)',
                        fontWeight: '600',
                        color: 'var(--color-neutral-700)'
                      }}
                    >
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows="5"
                      style={{
                        ...inputStyle,
                        resize: 'vertical'
                      }}
                      onFocus={(e) =>
                        Object.assign(e.target.style, inputFocusStyle)
                      }
                      onBlur={(e) => {
                        e.target.style.borderColor = 'var(--color-neutral-200)'
                        e.target.style.boxShadow = 'none'
                      }}
                    />
                  </motion.div>

                  {/* Error Message */}
                  {error && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      style={{
                        marginBottom: 'var(--space-lg)',
                        padding: 'var(--space-md)',
                        background: '#fee',
                        border: '2px solid #fcc',
                        borderRadius: 'var(--radius-md)',
                        color: '#c33',
                        fontSize: 'var(--text-sm)'
                      }}
                    >
                      {error}
                    </motion.div>
                  )}

                  {/* Submit Button */}
                  <motion.button
                    variants={staggerItemVariants}
                    type="submit"
                    disabled={isSubmitting}
                    whileHover={
                      prefersReducedMotion || isSubmitting
                        ? {}
                        : { scale: 1.02 }
                    }
                    whileTap={
                      prefersReducedMotion || isSubmitting
                        ? {}
                        : { scale: 0.98 }
                    }
                    style={{
                      width: '100%',
                      padding: '1rem 2rem',
                      fontSize: 'var(--text-lg)',
                      fontWeight: '600',
                      color: 'white',
                      background: isSubmitting
                        ? 'var(--color-neutral-400)'
                        : 'var(--gradient-cta)',
                      border: 'none',
                      borderRadius: 'var(--radius-lg)',
                      cursor: isSubmitting ? 'not-allowed' : 'pointer',
                      boxShadow: 'var(--shadow-orange)',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    {isSubmitting ? 'Sending...' : 'Request a Demo'}
                  </motion.button>
                </StaggerContainer>
              </motion.div>
            ) : (
              <motion.div
                key="success"
                initial={
                  prefersReducedMotion
                    ? { opacity: 0 }
                    : { opacity: 0, scale: 0.9 }
                }
                animate={
                  prefersReducedMotion
                    ? { opacity: 1 }
                    : { opacity: 1, scale: 1 }
                }
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4 }}
                style={{
                  background: 'white',
                  padding: 'var(--space-3xl)',
                  borderRadius: 'var(--radius-xl)',
                  boxShadow: 'var(--shadow-lg)',
                  textAlign: 'center'
                }}
              >
                <motion.div
                  initial={prefersReducedMotion ? {} : { scale: 0 }}
                  animate={prefersReducedMotion ? {} : { scale: 1 }}
                  transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                  style={{
                    width: '80px',
                    height: '80px',
                    margin: '0 auto var(--space-lg) auto',
                    background: 'var(--color-accent-500)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <svg
                    width="40"
                    height="40"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M16.7071 5.29289C17.0976 5.68342 17.0976 6.31658 16.7071 6.70711L8.70711 14.7071C8.31658 15.0976 7.68342 15.0976 7.29289 14.7071L3.29289 10.7071C2.90237 10.3166 2.90237 9.68342 3.29289 9.29289C3.68342 8.90237 4.31658 8.90237 4.70711 9.29289L8 12.5858L15.2929 5.29289C15.6834 4.90237 16.3166 4.90237 16.7071 5.29289Z"
                      fill="white"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </motion.div>
                <h3 style={{ marginBottom: 'var(--space-md)' }}>
                  Thank You!
                </h3>
                <p
                  style={{
                    fontSize: 'var(--text-lg)',
                    color: 'var(--color-neutral-600)'
                  }}
                >
                  We've received your message and will be in touch shortly to
                  schedule your demo.
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  )
}

export default ContactDemo
